import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import '../styles/Products.css'

const BASE_URL = 'https://api.yourhood.ge'
const PER_PAGE = 24

export default function Products({ selectedCategory, addToCart, searchText = '' }) {
  const [products, setProducts] = useState([])
  const [page, setPage] = useState(1)
  const [totalProducts, setTotalProducts] = useState(0)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const sectionRef = useRef(null)

  const totalPages = Math.ceil(totalProducts / PER_PAGE)

  useEffect(() => {
    setPage(1)
  }, [selectedCategory, searchText])

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({
      Page: page,
      PageSize: PER_PAGE,
      ...(selectedCategory && { CategoryID: selectedCategory }),
      ...(searchText.trim() && { SearchText: searchText.trim() }),
    })
    fetch(`${BASE_URL}/api/Product/protudcts?${params}`, {
      headers: { 'X-Language': 'ka' }
    })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data)
          ? data
          : (data.items || data.products || data.data || [])
        setProducts(list)
        setTotalProducts(list.length < PER_PAGE ? (page - 1) * PER_PAGE + list.length : page * PER_PAGE + 1)
        setLoading(false)
      })
      .catch(err => {
        console.error('Products error:', err)
        setProducts([])
        setLoading(false)
      })
  }, [page, selectedCategory, searchText])

  const goTo = (p) => {
    setPage(p)
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handleAddToCart = (e, product) => {
    e.stopPropagation()
    addToCart({
      id: product.id,
      name: product.title,
      category: product.categoryName,
      price: product.salePrice ?? product.price,
      originalPrice: product.price,
      image: product.imageUrls?.[0] || `https://picsum.photos/seed/${product.id}/600/800`,
      size: product.availableSizes?.[0] || 'M'
    })
  }

  return (
    <section className="products-section" ref={sectionRef} id='products'>
      {loading ? (
        <div className="products-loading">იტვირთება...</div>
      ) : products.length === 0 ? (
        <div className="products-loading">
          {searchText ? `"${searchText}" — პროდუქტი ვერ მოიძებნა` : 'პროდუქტი ვერ მოიძებნა'}
        </div>
      ) : (
        <div className="products-grid">
          {products.map(product => (
            <div
              key={product.id}
              className="product-card"
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <div className="product-image-wrap">
                <img
                  src={product.imageUrls?.[0] || `https://picsum.photos/seed/${product.id}/600/800`}
                  alt={product.title}
                  className="product-image"
                  loading="lazy"
                />

                {/* Sale badge */}
                {product.salePrice != null && (
                  <span className="product-sale-badge">SALE</span>
                )}

                <div className="product-actions">
                  <button
                    className="product-action-btn"
                    title="კალათა"
                    onClick={(e) => handleAddToCart(e, product)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 01-8 0"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="product-info">
                <span className="product-category">{product.categoryName}</span>
                <h3 className="product-name">{product.title}</h3>

                {/* Price — sale or normal */}
                <div className="product-price-row">
                  {product.salePrice != null ? (
                    <>
                      <span className="product-price product-price--sale">₾{product.salePrice}</span>
                      <span className="product-price product-price--original">₾{product.price}</span>
                    </>
                  ) : (
                    <span className="product-price">₾{product.price}</span>
                  )}
                </div>

                {/* Sizes */}
                {product.availableSizes?.length > 0 && (
                  <div className="product-sizes">
                    {product.availableSizes.map(size => (
                      <span key={size} className="product-size-chip">{size}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={() => goTo(page - 1)} disabled={page === 1}>←</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              className={`page-btn ${p === page ? 'page-btn--active' : ''}`}
              onClick={() => goTo(p)}
            >
              {p}
            </button>
          ))}
          <button className="page-btn" onClick={() => goTo(page + 1)} disabled={page === totalPages}>→</button>
        </div>
      )}
    </section>
  )
}